import { AddressCard } from '@/components/shared/address-card';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/context/auth-context';
import {
  useGetUserDetailQuery,
  type Address,
} from '@/hooks/queries/use-get-user-detail.query';
import {
  EditAddressFormDialog,
  type EditAddressFormData,
} from '@/features/consumer/edit-address-form-dialog';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useDeleteUserDeleteAddressMutation } from '@/hooks/mutations/use-delete-user-delete-address.mutation';
import { usePutUsersAddressesMutation } from '@/hooks/mutations/use-put-users-addresses.mutation';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useState } from 'react';
import { useTrack } from '@/features/tracking/useTrack';
import { useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';

function formatSavedAddressLine(address: Address): string {
  return [
    address.address,
    [address.city, address.state].filter(Boolean).join(', '),
    address.zipcode,
    address.country,
  ]
    .filter(part => part != null && String(part).trim() !== '')
    .join(', ');
}

export function SavedAddressWidgetDashboard() {
  const { user } = useAuth();
  const track = useTrack();
  const userId = user?.userid;

  // Dialog states
  const {
    isOpen: isEditOpen,
    toggle: toggleEdit,
    close: closeEdit,
  } = useDisclosure();

  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // State for edit dialog
  const [addressToEdit, setAddressToEdit] = useState<Address | null>(null);

  const {
    data: userData,
    isLoading,
    refetch: refetchUserDetail,
  } = useGetUserDetailQuery(userId || '', {
    enabled: !!userId,
  });

  // Mutations
  const deleteAddressMutation = useDeleteUserDeleteAddressMutation();
  const setDefaultAddressMutation = usePutUsersAddressesMutation();

  // Get addresses from user detail query data
  const addresses = userData?.details?.addresses || [];
  const defaultAddressId = userData?.default_address?.id;

  const navigate = useNavigate();
  const handleBookAddress = (address: Address) => {
    track('click', {
      eventAction: 'clickButton',
      eventCategory: 'bookButton',
      eventDescription: 'clickBookSavedAddress',
      eventLabel: 'startBooking',
    });
    const hasAddress = address.address !== '';
    if (!hasAddress) {
      setAddressToEdit(address);
      toggleEdit();
      return;
    }
    return navigate({
      to: '/booking',
      search: { address_id: address.id },
    });
  };
  // Event handlers
  const handleEditAddress = async (_data: EditAddressFormData) => {
    // Refetch user data to get updated addresses
    await refetchUserDetail();
  };

  const handleEditClick = (address: Address) => {
    setAddressToEdit(address);
    toggleEdit();
  };

  const handleDeleteClick = (addressId: string, addressName: string) => {
    setAddressToDelete({ id: addressId, name: addressName });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!addressToDelete) return;

    try {
      await deleteAddressMutation
        .mutateAsync({
          addressId: addressToDelete.id,
        })
        .then(() => {
          refetchUserDetail();
          setDeleteDialogOpen(false);
          setAddressToDelete(null);
        });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to delete address:', error);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setAddressToDelete(null);
  };

  const handleSetAsDefault = async (addressId: string) => {
    try {
      // Find the address to update
      const addressToUpdate = addresses.find(addr => addr.id === addressId);
      if (!addressToUpdate) {
        return;
      }

      await setDefaultAddressMutation.mutateAsync({
        addressId,
        payload: {
          address: addressToUpdate.address || '',
          zipcode: addressToUpdate.zipcode || '',
          city: addressToUpdate.city || '',
          state: addressToUpdate.state || '',
          country: addressToUpdate.country || '',
          default_address: true,
          address_name: addressToUpdate.address_name || '',
          bedroom: addressToUpdate.bedroom || 0,
          bathroom: addressToUpdate.bathroom || 0,
          people: addressToUpdate.people || 0,
          pet: addressToUpdate.pet || 0,
          cleaning_area: addressToUpdate.cleaning_area || '',
          house_desc: addressToUpdate.house_desc || '',
          latitude: 0,
          longitude: 0,
        },
      });
      // Refetch user data to get updated addresses
      await refetchUserDetail();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to set default address:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
          <p className="text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <section className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-lg font-bold text-black dark:text-white">
          Saved Address
        </h2>
        <Button
          type="button"
          onClick={() => {
            track('click', {
              eventAction: 'clickButton',
              eventCategory: 'bookButton',
              eventDescription: 'clickBookNextCleaning',
              eventLabel: 'startBooking',
            });
            navigate({ to: '/booking' });
          }}
          className="w-full rounded-full border-0 bg-linear-to-r from-orange-500 to-orange-600 px-6 font-semibold text-white shadow-none hover:bg-[#cf632e] sm:w-auto"
        >
          Book Next Cleaning
        </Button>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {addresses.length === 0 ? (
          <Card className="rounded-2xl border-0 bg-[#F0F0F0] dark:bg-neutral-900">
            <div className="py-8 text-center text-muted-foreground">
              <p>No saved addresses found.</p>
              <p className="text-sm">Add your first address to get started.</p>
            </div>
          </Card>
        ) : (
          addresses.map(address => (
            <div
              key={address.id}
              className="rounded-2xl bg-[#F0F0F0] p-5 dark:bg-neutral-900 dark:ring-1 dark:ring-neutral-800"
            >
              <div className="mb-3 flex items-start justify-between gap-2">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <h3 className="text-lg font-bold text-black dark:text-white">
                    {address.address_name || 'Home'}
                  </h3>
                  {defaultAddressId === address.id ? (
                    <span className="shrink-0 rounded-full border border-[#ED7034]/50 bg-white px-2.5 py-0.5 text-xs font-semibold text-[#ED7034] dark:bg-neutral-800 dark:text-[#ED7034]">
                      Default
                    </span>
                  ) : null}
                </div>
                <AddressCard.EllipsisDropdown>
                  <AddressCard.EllipsisDropdown.Item
                    onClick={() => handleEditClick(address)}
                  >
                    Edit
                  </AddressCard.EllipsisDropdown.Item>
                  {defaultAddressId !== address.id && (
                    <AddressCard.EllipsisDropdown.Item
                      onClick={() => handleSetAsDefault(address.id)}
                      disabled={setDefaultAddressMutation.isPending}
                    >
                      Set as Default
                    </AddressCard.EllipsisDropdown.Item>
                  )}
                  <AddressCard.EllipsisDropdown.Item
                    variant="destructive"
                    onClick={() =>
                      handleDeleteClick(
                        address.id,
                        address.address_name || 'Home'
                      )
                    }
                  >
                    Delete
                  </AddressCard.EllipsisDropdown.Item>
                </AddressCard.EllipsisDropdown>
              </div>
              <p className="mb-6 text-base font-normal leading-relaxed text-[#666666] dark:text-gray-400">
                {formatSavedAddressLine(address)}
              </p>
              <Button
                type="button"
                onClick={() => handleBookAddress(address)}
                className="rounded-full border-0 bg-linear-to-r from-orange-500 to-orange-600 px-6 font-bold text-white shadow-none hover:bg-[#cf632e]"
              >
                Book
              </Button>
            </div>
          ))
        )}
      </div>

      {/* Edit Address Dialog */}
      {addressToEdit && (
        <EditAddressFormDialog
          open={isEditOpen}
          onSuccess={handleEditAddress}
          onToggle={toggleEdit}
          onClose={closeEdit}
          address={addressToEdit}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Address</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{addressToDelete?.name}"? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDelete}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleteAddressMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 text-white"
            >
              {deleteAddressMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
